import React from 'react';

/**
 * PaginationButton Component - Atomic Level
 * 
 * Specialized button for pagination controls
 * Handles page numbers and navigation buttons
 * 
 * Features:
 * - Active/inactive states
 * - Disabled state
 * - Icon support
 * - Accessible
 */

export interface PaginationButtonProps {
  /** Button content */
  children: React.ReactNode;
  /** Whether button is active/selected */
  active?: boolean;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Button type */
  variant?: 'page' | 'nav';
  /** ARIA label */
  ariaLabel?: string;
  /** Additional CSS classes */
  className?: string;
}

export const PaginationButton: React.FC<PaginationButtonProps> = ({
  children,
  active = false,
  disabled = false,
  onClick,
  variant = 'page',
  ariaLabel,
  className = '',
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 disabled:opacity-40 disabled:cursor-not-allowed';
  
  const variantClasses = {
    page: `min-w-[2.5rem] h-10 px-3 rounded-lg text-sm ${
      active
        ? 'bg-[#EAB308] text-white shadow-md'
        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
    }`,
    nav: 'min-w-[2.5rem] h-10 px-3 rounded-lg text-sm bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-[#EAB308]',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={active ? 'page' : undefined}
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

export default PaginationButton;

